import React from 'react';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {UseArtifactsResult} from 'llm/chat-library/useArtifacts';
import type {ChatSidebarData} from 'llm/chat-library/useMessageThread';

import {AnalysisWorkspaceLayout} from './analysis-workspace/AnalysisWorkspaceLayout';
import {LegacyDataImportSideBySideLayout} from './LegacyDataImportSideBySideLayout';
import {RightSidebarLayout} from './right-sidebar/RightSidebarLayout';
import {SideBySideLayout} from './SideBySideLayout';

/**
 * Props passed to chat layout components.
 */
export interface ChatLayoutProps {
  /** The chat content (message thread + input) */
  chatContent: JSX.Element;
  /** Artifacts from useArtifacts hook */
  artifacts: UseArtifactsResult;
  /** Canonical sidebar data loaded with the chat session. */
  sidebarData: ChatSidebarData | null;
  /** Whether the document is editable */
  isEditable: boolean;
  /**
   * Whether the layout is rendering in a narrow (non-expanded) panel where there is no room for a
   * side-by-side split. Layouts that support it (RightSidebarLayout) render their artifact view over
   * the full panel width instead of beside the chat. Other layouts ignore this.
   */
  fullWidth?: boolean;
}

/**
 * A component that renders a chat layout.
 */
export type ChatLayoutComponent = React.ComponentType<ChatLayoutProps>;

/**
 * Options that influence which layout is selected, beyond the chat type.
 */
export interface ChatLayoutOptions {
  /** Whether the LLM_ENABLE_RIGHT_SIDEBAR rollout is enabled. */
  rightSidebarEnabled?: boolean;
}

/**
 * Get the appropriate layout component for a chat type.
 *
 * Most chat types use the standard two-panel layout (chat + artifact).
 * LDI uses a custom three-panel layout (chat + document + source file).
 *
 * When the right-sidebar rollout is enabled, chat types that would otherwise use the standard
 * two-panel layout instead dock artifacts in a fixed-width right sidebar. The ANALYSIS and
 * LEGACY_DATA_IMPORT layouts are unchanged by the rollout in this first version.
 */
export function getChatLayout(chatType: ChatType, options: ChatLayoutOptions = {}): ChatLayoutComponent {
  switch (chatType) {
    case ChatType.ANALYSIS:
      return AnalysisWorkspaceLayout;
    case ChatType.LEGACY_DATA_IMPORT:
      return LegacyDataImportSideBySideLayout;
    default:
      return options.rightSidebarEnabled ? RightSidebarLayout : SideBySideLayout;
  }
}

/**
 * Whether this chat type uses the right-sidebar layout. The right sidebar renders even in the narrow
 * (non-expanded) panel — taking the full width — so callers that gate on expansion need to know when
 * the layout should still mount.
 */
export function isRightSidebarLayout(chatType: ChatType, options: ChatLayoutOptions = {}): boolean {
  return getChatLayout(chatType, options) === RightSidebarLayout;
}
